import React from "react";
import { Group, Rect, Circle, Ellipse, Line, Text } from "react-konva";
import { posicionesSillas, RADIO_ASIENTO, ANCHO_NOMBRE_FILA } from "../../domain/mapa/geometria";
import { capacidadSector, conLugares, COLOR_NEUTRO } from "../../domain/mapa/mapa";
import { COLOR, conAlfa } from "./estilos";

const Asiento = ({ x, y, color, vendido, etiqueta }) => (
  <>
    <Circle
      x={x}
      y={y}
      radius={RADIO_ASIENTO}
      fill={vendido ? COLOR.vendido : "#ffffff"}
      stroke={vendido ? COLOR.vendido : color}
      strokeWidth={2}
      listening={false}
      perfectDrawEnabled={false}
    />
    {etiqueta && (
      <Text
        x={x - RADIO_ASIENTO}
        y={y - 4}
        width={RADIO_ASIENTO * 2}
        align="center"
        text={etiqueta}
        fontSize={etiqueta.length > 2 ? 6 : 8}
        fill={vendido ? "#ffffff" : COLOR.titulo}
        listening={false}
        perfectDrawEnabled={false}
      />
    )}
  </>
);

const MesaFigura = ({ mesa, color, detalle, interactiva, bajar = true, arrastrable = true, seleccionada, onSeleccionar, onMover, onMoverFin }) => {
  const sillas = posicionesSillas(mesa);
  // una mesa vendida (entera o todas sus sillas) se ve gris, como sus sillas
  const vendida = mesa.vendida || (mesa.sillas.length > 0 && mesa.sillas.every((x) => x.vendido));
  const tono = vendida ? COLOR.vendido : color;
  const borde = seleccionada ? { stroke: COLOR.acento, strokeWidth: 3 } : { stroke: tono, strokeWidth: 1.5 };
  return (
    <Group
      id={mesa.clave}
      x={mesa.x}
      y={mesa.y}
      rotation={mesa.rotacion || 0}
      listening={Boolean(interactiva)}
      draggable={Boolean(interactiva && arrastrable)}
      onMouseDown={(e) => {
        // sin bajar, el clic es de la ubicación (se elige y se mueve entera)
        if (!bajar) return;
        e.cancelBubble = true;
        onSeleccionar?.(e.evt.shiftKey);
      }}
      onTap={(e) => {
        if (!bajar) return;
        e.cancelBubble = true;
        onSeleccionar?.(false);
      }}
      onDragStart={(e) => {
        e.cancelBubble = true;
      }}
      onDragMove={(e) => {
        e.cancelBubble = true;
        onMover?.(e);
      }}
      onDragEnd={(e) => {
        e.cancelBubble = true;
        onMoverFin?.(e);
      }}
    >
      {mesa.forma === "rectangular" ? (
        <Rect width={mesa.ancho} height={mesa.alto} cornerRadius={6} fill={conAlfa(tono, vendida ? 0.45 : 0.18)} {...borde} />
      ) : (
        <Ellipse
          x={mesa.ancho / 2}
          y={mesa.alto / 2}
          radiusX={mesa.ancho / 2}
          radiusY={mesa.alto / 2}
          fill={conAlfa(tono, vendida ? 0.45 : 0.18)}
          {...borde}
        />
      )}
      <Text
        text={mesa.nombre}
        width={mesa.ancho}
        y={mesa.alto / 2 - 6}
        align="center"
        fontSize={11}
        fontStyle="bold"
        fill={COLOR.titulo}
      />
      {sillas.map((p, i) => (
        <Asiento
          key={i}
          x={p.x}
          y={p.y}
          color={color}
          vendido={mesa.vendida || mesa.sillas[i]?.vendido}
          etiqueta={detalle ? mesa.sillas[i]?.etiqueta : null}
        />
      ))}
    </Group>
  );
};

// Contorno: sin fondo ni borde queda una guía tenue, sólo en el editor, para
// poder elegir la ubicación
const Contorno = ({ sector, color, seleccionado }) => {
  const sinFondo = sector.editor?.ocultarFondo;
  // sin fondo y con lugares, la caja no toma clics: se elige por sus filas o
  // mesas (así no tapa a otra ubicación que quede abajo). Ya elegida, sí, para
  // poder arrastrarla desde cualquier parte.
  const props = sinFondo
    ? {
        fill: "rgba(255,255,255,0.01)",
        stroke: seleccionado ? color : "#d4d4d8",
        strokeWidth: seleccionado ? 2 : 1,
        dash: [4, 4],
        listening: !conLugares(sector) || Boolean(seleccionado),
      }
    : {
        fill: conAlfa(color, conLugares(sector) ? 0.06 : 0.16),
        stroke: color,
        strokeWidth: seleccionado ? 3 : 1.5,
        dash: conLugares(sector) ? [8, 6] : undefined,
      };
  if (sector.forma?.length > 2) {
    return <Line name="contorno" points={sector.forma.flatMap((p) => [p.x, p.y])} closed {...props} />;
  }
  return <Rect name="contorno" width={sector.ancho} height={sector.alto} cornerRadius={8} {...props} />;
};

// MarcoFila: el borde de la fila elegida, pegado a los asientos y siguiendo
// su curva; sin elegir es sólo la zona para el clic (los asientos no escuchan
// eventos, para que el plano sea liviano)
const MarcoFila = ({ fila, elegida }) => {
  if (fila.asientos.length === 0) return null;
  const puntos = fila.asientos.flatMap((a) => [a.x, a.y]);
  const ancho = 2 * RADIO_ASIENTO + 6;
  if (fila.asientos.length === 1) {
    const [a] = fila.asientos;
    return <Circle x={a.x} y={a.y} radius={ancho / 2} stroke={elegida ? COLOR.acento : undefined} strokeWidth={2} fill="rgba(0,0,0,0.001)" />;
  }
  const linea = { points: puntos, lineCap: "round", lineJoin: "round", tension: 0 };
  if (!elegida) return <Line {...linea} stroke="rgba(0,0,0,0.001)" strokeWidth={ancho} />;
  return (
    <>
      <Line {...linea} stroke={COLOR.acento} strokeWidth={ancho + 4} listening={false} />
      <Line {...linea} stroke="#ffffff" strokeWidth={ancho} />
    </>
  );
};

export const SectorFigura = ({
  sector,
  seleccionado,
  enGrupo = false,
  arrastrable,
  detalle,
  mesasSeleccionadas = [],
  filasSeleccionadas = [],
  filasArrastrables = true,
  colorForzado = null,
  onSeleccionar,
  onSeleccionarFila,
  onMoverFilaFin,
  onMoverFin,
  onMover,
  onSeleccionarMesa,
  onMoverMesa,
  onMoverMesaFin,
}) => {
  // "filas" oculta los números de asiento; "nada", también los nombres de fila
  const numeracion = sector.editor?.numeracion;
  const color = colorForzado || COLOR_NEUTRO;
  const lugares = conLugares(sector);
  // en el diseño, como las filas: el primer clic elige la ubicación y el
  // segundo la mesa, que recién elegida se arrastra sola. Para asignar
  // tarifas cada mesa se elige con un clic.
  const mesasInteractivas = Boolean(onSeleccionarMesa);
  // elegida junto con otras, un clic sobre sus lugares la arrastra con el grupo
  const elegidaSola = seleccionado && !enGrupo;
  const bajarAMesa = !filasArrastrables || elegidaSola || mesasSeleccionadas.length > 0;
  return (
    <Group
      id={sector.clave}
      x={sector.x}
      y={sector.y}
      rotation={sector.rotacion || 0}
      draggable={arrastrable}
      onMouseDown={onSeleccionar}
      onTap={onSeleccionar}
      onDragMove={onMover}
      onDragEnd={onMoverFin}
    >
      <Contorno sector={sector} color={color} seleccionado={seleccionado} />
      {sector.filas.map((fila, indice) => {
        const elegida = filasSeleccionadas.includes(indice);
        return (
          <Group
            key={fila.nombre || indice}
            draggable={elegida && filasArrastrables && filasSeleccionadas.length === 1}
            onMouseDown={(e) => {
              // el segundo clic sobre una ubicación elegida baja a la fila
              if (!onSeleccionarFila || (!elegidaSola && filasSeleccionadas.length === 0)) return;
              e.cancelBubble = true;
              onSeleccionarFila(indice, e.evt.shiftKey);
            }}
            onDragEnd={(e) => {
              const { x, y } = e.target.position();
              e.target.position({ x: 0, y: 0 });
              if (x || y) onMoverFilaFin?.(indice, Math.round(x), Math.round(y));
            }}
          >
            {onSeleccionarFila && <MarcoFila fila={fila} elegida={elegida} />}
            {fila.nombre && fila.asientos.length > 0 && numeracion !== "nada" && (
              <Text
                x={fila.asientos[0].x - RADIO_ASIENTO - ANCHO_NOMBRE_FILA}
                y={fila.asientos[0].y - 6}
                width={ANCHO_NOMBRE_FILA - 4}
                align="right"
                text={fila.nombre}
                fontSize={11}
                fontStyle="bold"
                fill={COLOR.textoSuave}
                listening={false}
              />
            )}
            {fila.asientos.map((a, i) => (
              <Asiento key={`${fila.nombre}-${i}`} x={a.x} y={a.y} color={color} vendido={a.vendido} etiqueta={detalle && !numeracion ? a.etiqueta : null} />
            ))}
          </Group>
        );
      })}
      {sector.mesas.map((mesa) => (
        <MesaFigura
          key={mesa.clave}
          mesa={mesa}
          color={color}
          detalle={detalle && !numeracion}
          interactiva={mesasInteractivas}
          seleccionada={mesasSeleccionadas.includes(mesa.clave)}
          bajar={bajarAMesa}
          arrastrable={filasArrastrables && mesasSeleccionadas.length === 1 && mesasSeleccionadas[0] === mesa.clave}
          onSeleccionar={(sumar) => onSeleccionarMesa?.(mesa, sumar)}
          onMover={onMoverMesa}
          onMoverFin={(e) => onMoverMesaFin?.(mesa, e)}
        />
      ))}
      {sector.decorativos.map((d) => (
        <DecorativoFigura key={d.clave} decorativo={d} />
      ))}
      <Text
        visible={!sector.editor?.ocultarNombre}
        text={sector.nombre}
        width={sector.ancho}
        y={lugares ? 6 : sector.alto / 2 - 16}
        align="center"
        fontSize={15}
        fontStyle="bold"
        fill={COLOR.titulo}
        listening={false}
      />
      {!lugares && (
        <Text
          text={`${capacidadSector(sector)} lugares`}
          width={sector.ancho}
          y={sector.alto / 2 + 4}
          align="center"
          fontSize={12}
          fill={COLOR.textoSuave}
          listening={false}
        />
      )}
    </Group>
  );
};

export const DecorativoFigura = ({ decorativo: d, seleccionable, arrastrable, onSeleccionar, onMover, onMoverFin }) => {
  const eventos = seleccionable
    ? { onMouseDown: onSeleccionar, onTap: onSeleccionar, onDragMove: onMover, onDragEnd: onMoverFin }
    : { listening: false };

  let figura = null;
  if (d.tipo === "texto") {
    figura = <Text text={d.texto || " "} fontSize={d.font_size || 18} fill={d.color} fontStyle="bold" />;
  } else if (d.tipo === "rectangulo") {
    figura = <Rect width={d.ancho} height={d.alto} cornerRadius={4} fill={d.color} />;
  } else if (d.tipo === "circulo") {
    figura = <Ellipse x={d.ancho / 2} y={d.alto / 2} radiusX={d.ancho / 2} radiusY={d.alto / 2} fill={d.color} />;
  } else if (d.tipo === "linea") {
    figura = (
      <Line
        points={[0, 0, (d.punto_final_x ?? d.x) - d.x, (d.punto_final_y ?? d.y) - d.y]}
        stroke={d.color}
        strokeWidth={3}
        hitStrokeWidth={14}
        lineCap="round"
      />
    );
  }

  return (
    <Group id={d.clave} x={d.x} y={d.y} rotation={d.rotacion || 0} draggable={Boolean(seleccionable && arrastrable)} {...eventos}>
      {figura}
    </Group>
  );
};
